import { Fragment, useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Button from 'react-bootstrap/Button';
import styled from 'styled-components';

import { createPortal } from 'react-dom';
import { getHexagonFeatures } from '@utils/api';
import SidebarTemplate from '../../../sidebars/sidebarElements/SidebarTemplate';

import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import Alert from '@components/alert/Alert';
import Range from '@components/rangeSlider/Range';
import Chart from './Chart';
import { isNum, limitDecimal } from '@utils/helpers';
import Input from '@components/form/Input';
import Loader from '@components/loading/Loader';
import { formatLength, formatArea } from '@utils/measurementSystem';
import useDebounce from '@hooks/useDebounce';

const StyledRange = styled(Range)`
	padding-inline: 10px;
`;
const StyledChart = styled(Chart)`
	padding-inline: 10px;
`;

const Wrapper = styled.div`
	position: relative;
	padding: 20px 20px 200px;
	display: grid;
	gap: 20px;
`;

const ClassWrapper = styled.div`
	overflow: clip;

	padding-bottom: 16px;
	border-radius: 6px;

	background: #222222;
	color: ${({ theme }) => theme.colors.textColor};
	border: 1px solid rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.2);
`;

const ClassHeader = styled.div`
	border-radius: inherit;
	overflow: hidden;
	position: sticky;
	top: 0;
	z-index: 3;

	display: flex;
	justify-content: space-between;

	padding-top: 20px;
	padding-bottom: 24px;
	padding-inline: 16px;

	background: linear-gradient(to top, transparent 0%, #222222);
	backdrop-filter: blur(3px);
`;

const ClassLabel = styled.h3`
	margin: 0;
	text-transform: capitalize;
	font-size: 1.25rem;
`;

const ClassCountInfo = styled.p`
	margin: 0;
	font-size: 0.85rem;
	color: #aaa;
`;

const PropertiesWrapper = styled.div`
	padding-inline: 16px;
`;

const PropertyWrapper = styled.div`
	margin-bottom: 32px;
`;

const PropertyLabel = styled.p`
	margin: 0;
	margin-bottom: 12px;
	margin-top: 26px;
	line-height: 0.8;
	font-size: 1rem;
	font-weight: 200;
	letter-spacing: 1px;
	text-transform: uppercase;
	text-align: center;
	color: #aaa;
`;

const ButtonWrapper = styled.div`
	position: sticky;
	bottom: 0;
	z-index: 3;
	padding-top: 30px;
	padding-bottom: 20px;
	margin-top: -20px;
	background: linear-gradient(
		to bottom,
		transparent 0%,
		${({ theme }) => theme.colors.background} 20px
	);

	transform: translateY(100%);
	animation: slideUp 0.75s ease-out forwards;
	@keyframes slideUp {
		to {
			transform: translateY(0);
		}
	}

	display: grid;
	grid-auto-flow: column;
	gap: 10px;
`;

const InputWrapper = styled.div`
	margin-top: 12px;
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
	justify-content: space-between;
`;

const getDecimalCount = num => {
	if (Number.isInteger(num)) return 0;
	const numStr = String(num);
	const i = numStr.indexOf('.');

	if (i === -1) return 0;
	else return numStr.length - numStr.indexOf('.') - 1;
};

const getDiameterFromArea = area => Math.sqrt(area / Math.PI) * 2;

const propertyConfigMap = {
	confidence: {
		step: 0.01,
		formatFn: value => [Math.round(value * 100), '%'],
		inputDisplay: value => Math.round(value * 100),
		inputSet: value => value / 100,
	},
	height: { step: 0.1, formatFn: formatLength },
	area: { step: 0.1, formatFn: formatArea },
	diameter: { step: 0.1, formatFn: formatLength },
};

const NumInput = ({ label, value, setValue, step, min, max }) => {
	const [internalValue, setInternalValue] = useState(value);
	useEffect(() => setInternalValue(value), [value]);
	const stepDecimalCount = useMemo(() => getDecimalCount(step), [step]);

	const isInvalid = isNaN(internalValue) || internalValue > max || internalValue < min;
	const isTempValue = num => {
		if (num === '') return true;
		if (num > max || num < min) return true;
		return false;
	};

	const onChange = ({ target }) => {
		const changeValue = target.value;
		if (isTempValue(changeValue)) return setInternalValue(changeValue);

		const limitedNum = Math.max(Math.min(Number(changeValue), max), min);
		const decimalLimitedNum = limitDecimal(limitedNum, stepDecimalCount);

		setInternalValue(decimalLimitedNum);
		setValue(decimalLimitedNum);
	};

	return (
		<Input
			type="number"
			label={label}
			min={min}
			max={max}
			value={internalValue}
			step={step}
			onChange={onChange}
			isInvalid={isInvalid}
			noWrap
		/>
	);
};

const MinMaxInputs = ({ values, setValues, step, min, max, inputSet, inputDisplay }) => {
	[step, min, max] = [step, min, max].map(inputDisplay);
	const [minVal, maxVal] = values.map(inputDisplay);
	const setMin = val => setValues([inputSet(val), values[1]]);
	const setMax = val => setValues([values[0], inputSet(val)]);

	return (
		<InputWrapper>
			<NumInput
				label="Min"
				value={minVal}
				setValue={setMin}
				step={step}
				min={min}
				max={maxVal}
			/>
			<NumInput
				label="Max"
				value={maxVal}
				setValue={setMax}
				step={step}
				min={minVal}
				max={max}
			/>
		</InputWrapper>
	);
};

const PropertyFilter = ({
	classname,
	property,
	initialValues,
	step,
	min,
	max,
	formatFn,
	inputDisplay = value => value,
	inputSet = value => value,
}) => {
	const { detectionsFilter, detections, dispatch } = useProject();
	const [values, setValues] = useState(initialValues);

	// Apply draft filter to global state
	const applyDraft = useDebounce(([min2, max2]) => {
		const propertyRange = {};
		if (min2 !== min) propertyRange.min = min2;
		if (max2 !== max) propertyRange.max = max2;

		const filter = {
			...detectionsFilter,
			isDrafting: true,
			draft: {
				...detectionsFilter?.draft,
				[classname]: {
					...detectionsFilter?.draft?.[classname],
					[property]: propertyRange,
				},
			},
		};

		dispatch({ type: 'setDetectionsFilter', payload: filter });
	}, 300);

	const displayValueFn = v => formatFn(v).join('');
	const setValuesAndDraft = newValues => {
		setValues(newValues);
		applyDraft(newValues);
	};

	return (
		<PropertyWrapper>
			<PropertyLabel>{property}</PropertyLabel>
			<StyledChart
				classname={classname}
				property={property}
				detections={detections}
				displayValue={displayValueFn}
				min={min}
				max={max}
			/>
			<StyledRange
				values={values}
				onChange={setValues}
				onFinalChange={applyDraft}
				displayValue={displayValueFn}
				step={step}
				min={min}
				max={max}
			/>
			<MinMaxInputs
				values={values}
				setValues={setValuesAndDraft}
				step={step}
				min={min}
				max={max}
				inputDisplay={inputDisplay}
				inputSet={inputSet}
			/>
		</PropertyWrapper>
	);
};

const Filters = ({ filterOptions }) => {
	const { detectionsFilter, features, detections } = useProject();

	// Count totals and filtered per classname
	const countByClass = useMemo(() => {
		const counts = {};
		for (const detection of detections) {
			const cls = detection.properties.classname;
			counts[cls] = counts[cls] || { total: 0, filtered: 0 };
			counts[cls].total++;
		}
		for (const feature of Object.values(features)[0] ?? []) {
			const cls = feature.getProperties().classname;
			if (counts[cls]) counts[cls].filtered++;
		}
		return counts;
	}, [detections, features]);

	return Object.entries(filterOptions).map(([classname, metadata], i) => {
		const { filtered = '?', total = '?' } = countByClass[classname] || {};

		return (
			<ClassWrapper key={`${classname}-${i}`}>
				<ClassHeader>
					<ClassLabel>{classname}</ClassLabel>
					<ClassCountInfo>
						{filtered} of {total}
					</ClassCountInfo>
				</ClassHeader>
				<PropertiesWrapper>
					{Object.entries(metadata).map(([property, { min, max }], j) => {
						if (!isNum(min) || !isNum(max)) return null;

						const { step, formatFn, inputDisplay, inputSet } =
							propertyConfigMap[property];
						const stepDecimalCount = getDecimalCount(step);

						// Limit min/max to same decimal count as step
						if (stepDecimalCount < getDecimalCount(min))
							min = limitDecimal(min, stepDecimalCount);
						if (stepDecimalCount < getDecimalCount(max))
							max = limitDecimal(max, stepDecimalCount);

						// Use last applied filter values as initial values for the slider if it exists.
						const initialValues = [
							detectionsFilter?.applied?.[classname]?.[property]?.min ?? min,
							detectionsFilter?.applied?.[classname]?.[property]?.max ?? max,
						];

						return (
							<Fragment key={`${classname}-${property}-${i}-${j}`}>
								<PropertyFilter
									property={property}
									classname={classname}
									initialValues={initialValues}
									step={step}
									min={min}
									max={max}
									formatFn={formatFn}
									inputDisplay={inputDisplay}
									inputSet={inputSet}
								/>
								{j < Object.entries(metadata).length - 1 && <hr />}
							</Fragment>
						);
					})}
				</PropertiesWrapper>
			</ClassWrapper>
		);
	});
};

const FilterDetections = () => {
	const {
		project,
		pickedTask,
		hexagonSize,
		modifyLayersSidebarRef,
		detections,
		detectionsFilter,
		pickedModelClasses,
		dispatch,
	} = useProject();

	const { addToast } = useToast();
	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const [render, setRender] = useState(true);
	const { data, isLoading } = useQuery({
		queryKey: ['fetchHexagonsStatic', projectUuid, modelUuid, hexagonSize],
		queryFn: () => getHexagonFeatures(projectUuid, modelUuid, hexagonSize),
		enabled: !!projectUuid && !!modelUuid,
	});

	const detectionsMetadata = data?.metadata?.total?.detections ?? null;
	const filterOptions = useMemo(() => {
		const options = {};

		if (!detectionsMetadata) return options;
		for (const classname in detectionsMetadata) {
			const metadata = detectionsMetadata[classname];
			const { min_height, max_height, min_area, max_area } = metadata;

			const min_diameter = getDiameterFromArea(min_area);
			const max_diameter = getDiameterFromArea(max_area);

			options[classname] = {
				height: { min: min_height, max: max_height },
				area: { min: min_area, max: max_area },
				diameter: { min: min_diameter, max: max_diameter },
				confidence: { min: 0, max: 1 },
			};
		}

		return options;
	}, [data]);

	const applyFilters = () => {
		dispatch({
			type: 'setDetectionsFilter',
			payload: {
				...detectionsFilter,
				isDrafting: false,
				applied: detectionsFilter.draft,
			},
		});

		addToast({ id: 'filters-applied', title: `Filters Applied`, bg: 'success', delay: 2500 });
	};

	const resetFilters = () => {
		dispatch({
			type: 'setDetectionsFilter',
			payload: { isDrafting: false, draft: {}, applied: {} },
		});

		// Force re-render to reset sliders to initial values
		setRender(prev => !prev);
		addToast({ id: 'filters-reset', title: `Filters Reset`, bg: 'success', delay: 2500 });
	};

	const onHide = () => {
		dispatch({
			type: 'setDetectionsFilter',
			payload: {
				...detectionsFilter,
				isDrafting: false,
				draft: {},
			},
		});
	};

	if (!modifyLayersSidebarRef?.current) return null;
	const noDetections = detections.length === 0;
	const unknownError = !detectionsMetadata || !pickedModelClasses;
	return (
		<>
			{createPortal(
				<SidebarTemplate
					zindex="2"
					sidebarId="filterDetections"
					header="Filter Detections"
					width="large"
					onHide={onHide}
					closeable>
					<Wrapper>
						{isLoading ? (
							<Loader />
						) : unknownError ? (
							<Alert>
								An unknown error occurred while loading detection filters.
							</Alert>
						) : noDetections ? (
							<Alert>No detections available to modify.</Alert>
						) : (
							<>
								<Filters key={String(render)} filterOptions={filterOptions} />
								<ButtonWrapper>
									<Button onClick={resetFilters} variant="outline-secondary">
										Reset Filters
									</Button>
									<Button onClick={applyFilters} variant="success">
										Apply Filters
									</Button>
								</ButtonWrapper>
							</>
						)}
					</Wrapper>
				</SidebarTemplate>,
				modifyLayersSidebarRef.current
			)}
		</>
	);
};

export default FilterDetections;
